/**
 * 搜索数据增强器
 * 优先级：3
 * 职责：从 SearchContext 自动注入搜索数据
 *
 * ⚠️ 重要设计原则：
 * 1. 优先从 SearchContext 获取（业务代码主动管理）
 * 2. 如果 SearchContext 没有，从 URL 参数获取兜底（与旧版逻辑一致：params.q）
 * 3. 不再从旧版 getSearch() 获取，因为下线旧版后会失效
 * 4. 如果 URL 参数也没有，说明业务代码没有正确设置 SearchContext，应该修复业务代码
 */

import { trimStart } from "lodash";
import { parse } from "qs";
import { SearchContext } from "../context/SearchContext";
import { DataEnricher, EnrichContext } from "./types";

// ⚠️ 注意：不再从旧版 getSearch() 获取数据，因为下线旧版后会失效
// 如果 SearchContext 没有数据，只从 URL 参数获取兜底
// 如果 URL 参数也没有，说明业务代码没有正确设置 SearchContext，应该修复业务代码

export class SearchEnricher implements DataEnricher {
  name = "SearchEnricher";
  priority = 3;

  shouldEnrich(context: EnrichContext): boolean {
    const { page, options } = context;
    if (options?.enableSearch === false) return false;

    const SEARCH_PAGES = [
      "搜索结果页",
      "专题页",
      "主题页",
      "相似商用素材页",
      "相似素材推荐页",
      "采集详情页", // 采集详情页也需要搜索数据
    ];

    // 搜索相关页面需要搜索数据
    return SEARCH_PAGES.includes(page.page_nm);
  }

  enrich(context: EnrichContext): Record<string, any> {
    const { trackData } = context;

    // ⚠️ 重要：不再从 DOM 属性读取 search_word
    // ✅ 优先从 SearchContext 获取（统一数据源）
    // ⚠️ 重要：在获取 SearchContext 之前，先同步 URL 参数，确保 SearchContext 是最新的
    SearchContext.syncFromCurrentURL();
    const searchContext = SearchContext.get();
    if (searchContext) {
      // 如果调用方已经传了 search_word，使用调用方的（最高优先级）
      // 否则使用上下文中的
      // ⚠️ 重要：确保所有字段都被注入，即使 SearchContext 中没有这些字段，也要注入默认值（与旧版逻辑一致）
      const enrichedData: Record<string, any> = {};

      // 字符串字段：如果 trackData 中没有，则从 searchContext 获取
      if (trackData.search_word === undefined) {
        // 优先级：SearchContext > URL 参数（与旧版逻辑一致：trackData.search_word || params.q || searchData.search_word）
        // ⚠️ 注意：不再从旧版 getSearch() 获取，因为下线旧版后会失效
        enrichedData.search_word =
          searchContext.search_word || this.getSearchWordFromURL();
      }
      if (trackData.search_word_type === undefined) {
        // 优先级：SearchContext > undefined（URL 参数中没有 search_word_type）
        // ⚠️ 重要：对于 optional_fields，如果 SearchContext 中没有，不注入 undefined
        // 因为 Validator 会检查类型，undefined 会导致类型检查失败
        // 只有在 SearchContext 中明确存在时才注入
        if (searchContext.search_word_type !== undefined) {
          enrichedData.search_word_type = searchContext.search_word_type;
        }
      }
      if (trackData.search_type === undefined) {
        // 优先级：SearchContext > undefined（URL 参数中没有 search_type）
        // ⚠️ 重要：对于 optional_fields，如果 SearchContext 中没有，不注入 undefined
        // 因为 Validator 会检查类型，undefined 会导致类型检查失败
        // 只有在 SearchContext 中明确存在时才注入
        if (searchContext.search_type !== undefined) {
          enrichedData.search_type = searchContext.search_type;
        }
      }
      if (trackData.original_search_word === undefined) {
        // 优先级：SearchContext > URL 参数
        // ⚠️ 注意：不再从旧版 getSearch() 获取，因为下线旧版后会失效
        enrichedData.original_search_word =
          searchContext.original_search_word ||
          this.getOriginalSearchWordFromURL();
      }
      if (trackData.filter_one === undefined) {
        // ⚠️ 重要：filter_one 在 search 事件中必须是数组格式（与旧版一致）
        // SearchContext 中 filter_one 是字符串，需要转换为数组
        const filterOne = searchContext.filter_one;
        if (filterOne && filterOne !== "") {
          enrichedData.filter_one = [filterOne];
        } else {
          // 如果 SearchContext 中没有，留空，由 SearchEventEnricher 处理
          // 不在这里设置，避免覆盖 SearchEventEnricher 的逻辑
        }
      }

      // 数字字段：如果 trackData 中没有，则从 searchContext 获取
      // ⚠️ 重要：即使 searchContext.search_result_number 是 undefined，也要注入 undefined（与旧版逻辑一致）
      // 旧版逻辑：getSearchData 会直接赋值 searchData.search_result_number，即使它是 undefined
      if (trackData.search_result_number === undefined) {
        enrichedData.search_result_number = searchContext.search_result_number;
      }
      if (trackData.top_label_depth === undefined) {
        // 优先级：SearchContext > URL 参数 > 默认值 0
        // ⚠️ 注意：不再从旧版 getSearch() 获取，因为下线旧版后会失效
        enrichedData.top_label_depth =
          searchContext.top_label_depth ?? this.getTopLabelDepthFromURL() ?? 0;
      }

      // 数组字段：如果 trackData 中没有，则从 searchContext 获取，如果 searchContext 也没有，则使用空数组（与旧版逻辑一致）
      if (trackData.filter_ids === undefined) {
        enrichedData.filter_ids = searchContext.filter_ids || [];
      }
      if (trackData.top_label_list === undefined) {
        // 优先级：SearchContext > URL 参数
        // ⚠️ 注意：不再从旧版 getSearch() 获取，因为下线旧版后会失效
        enrichedData.top_label_list =
          searchContext.top_label_list || this.getTopLabelListFromURL() || [];
      }

      // ⚠️ 重要：即使 enrichedData 中的值是 undefined，也要注入（与旧版逻辑一致）
      // 因为如果 SearchContext 中没有这些字段，旧版 getSearch() 也可能没有，所以应该是 undefined
      // 但是，如果 SearchContext 中有这些字段（即使是空字符串或空数组），也要注入
      Object.keys(enrichedData).forEach((key) => {
        // 允许注入 undefined 值（与旧版逻辑一致：如果旧版 getSearch() 也没有，则为 undefined）
        trackData[key] = enrichedData[key];
      });

      if (process.env.NODE_ENV === "development") {
        console.log("[SearchEnricher] Injected search context:", {
          search_word: trackData.search_word,
          search_word_type: trackData.search_word_type,
          filter_ids: trackData.filter_ids,
          original_search_word: trackData.original_search_word,
          top_label_list: trackData.top_label_list,
          top_label_depth: trackData.top_label_depth,
          search_result_number: trackData.search_result_number,
          filter_one: trackData.filter_one,
        });
      }

      return trackData;
    }

    // ⚠️ 注意：如果 SearchContext 没有数据，从 URL 参数获取兜底（与旧版逻辑一致：params.q）
    // 这是为了确保在边缘场景下（如直接访问 URL）也能正确获取搜索数据
    // ⚠️ 重要：不再从旧版 getSearch() 获取，因为下线旧版后会失效
    // 如果 URL 参数也没有，说明业务代码没有正确设置 SearchContext，应该修复业务代码
    const enrichedData: Record<string, any> = {};

    // 从 URL 参数获取 search_word（与旧版逻辑一致：params.q）
    if (trackData.search_word === undefined) {
      enrichedData.search_word = this.getSearchWordFromURL();
    }

    // 其他字段也从 URL 参数获取兜底（如果 URL 中有）
    if (trackData.filter_ids === undefined) {
      enrichedData.filter_ids = this.getFilterIdsFromURL() || [];
    }
    if (trackData.original_search_word === undefined) {
      enrichedData.original_search_word = this.getOriginalSearchWordFromURL();
    }
    if (trackData.top_label_list === undefined) {
      enrichedData.top_label_list = this.getTopLabelListFromURL() || [];
    }
    if (trackData.top_label_depth === undefined) {
      enrichedData.top_label_depth = this.getTopLabelDepthFromURL() ?? 0;
    }

    // ⚠️ 注意：以下字段（search_word_type, search_result_number, search_type, filter_one）
    // 不在 URL 参数中，如果 SearchContext 没有数据，说明业务代码没有正确设置
    // 但是，为了确保数据完整性，即使 SearchContext 中没有，也要尝试从 URL 参数获取（虽然可能没有）
    // 如果 URL 参数也没有，则保持 undefined（与旧版逻辑一致：如果旧版 getSearch() 也没有，则为 undefined）

    // ⚠️ 重要：即使 enrichedData 中的值是 undefined，也要注入（与旧版逻辑一致）
    // 因为如果 SearchContext 中没有这些字段，旧版 getSearch() 也可能没有，所以应该是 undefined
    Object.keys(enrichedData).forEach((key) => {
      // 允许注入 undefined 值（与旧版逻辑一致：如果旧版 getSearch() 也没有，则为 undefined）
      trackData[key] = enrichedData[key];
    });

    return trackData;
  }

  /**
   * 从 URL 参数获取 original_search_word
   * ⚠️ 重要：如果 URL 中没有 original 参数，则使用当前 search_word 作为 original_search_word（与旧版逻辑一致）
   */
  private getOriginalSearchWordFromURL(): string | undefined {
    try {
      const params = parse(trimStart(window.location.search, "?"));
      // 如果 URL 中有 original 参数，使用它
      if (params.original) {
        return params.original as string | undefined;
      }
      // 如果 URL 中没有 original 参数，使用当前 search_word 作为 original_search_word（与旧版逻辑一致）
      // 旧版逻辑：getSearchData 中 original_search_word = searchData.original_search_word
      // 如果 searchData.original_search_word 没有，则使用 search_word
      const searchWord = params.q as string | undefined;
      return searchWord;
    } catch (e) {
      return undefined;
    }
  }

  /**
   * 从 URL 参数获取 top_label_list
   */
  private getTopLabelListFromURL(): string[] | undefined {
    try {
      const params = parse(trimStart(window.location.search, "?"));
      const topLabelList = params.top_label_list;
      if (typeof topLabelList === "string") {
        return topLabelList.split(",").filter(Boolean);
      }
      return undefined;
    } catch (e) {
      return undefined;
    }
  }

  /**
   * 从 URL 参数获取 top_label_depth
   */
  private getTopLabelDepthFromURL(): number | undefined {
    try {
      const params = parse(trimStart(window.location.search, "?"));
      const topLabelDepth = params.top_label_depth;
      if (topLabelDepth !== undefined) {
        let depth: number;
        if (typeof topLabelDepth === "string") {
          depth = parseInt(topLabelDepth, 10);
        } else if (typeof topLabelDepth === "number") {
          depth = topLabelDepth;
        } else {
          return undefined;
        }
        if (!isNaN(depth)) {
          return depth;
        }
      }
      return undefined;
    } catch (e) {
      return undefined;
    }
  }

  /**
   * 从 URL 参数获取 search_word
   */
  private getSearchWordFromURL(): string | undefined {
    try {
      const params = parse(trimStart(window.location.search, "?"));
      return params.q as string | undefined;
    } catch (e) {
      return undefined;
    }
  }

  /**
   * 从 URL 参数获取 filter_ids
   */
  private getFilterIdsFromURL(): string[] | undefined {
    try {
      const params = parse(trimStart(window.location.search, "?"));
      const filterIds = params.filter_ids;
      if (typeof filterIds === "string") {
        return filterIds.split(",").filter(Boolean);
      }
      return undefined;
    } catch (e) {
      return undefined;
    }
  }
}
